import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { FormButtonsComponent, FormButtonsConfig } from "@mipro/components";
import { IEpisode } from "@project/models/episodes/episode";
import { Diagnosis } from "@project/diagnosis/diagnosis";
import { MessageService } from "primeng/api";
import { EpisodeBaseComponent } from "./episode-base.component";
import { EpisodeService } from "./episode.service";
import { AppStorage } from "@bbwt/utils/app-storage";
import { WalkIn } from "@project/walk-ins/classes/walk-in";
import { IRavenIssue } from "@main/raven/models/raven-issue";
import { RavenControlComponent } from "@main/raven/raven-control.component";
import { RavenApiService } from "@main/raven/raven-api.service";
import { Message } from "@bbwt/classes/message";
import { Permissions } from "@main/permissions/permissions";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { DiagnosisControlComponent } from "@project/diagnosis/diagnosis-control.component";
import { GenericDiagnosisCodes } from "@project/diagnosis/generic-codes";
import { EpisodeCreatedFrom } from "@project/models/episodes/episode_createdFrom";
import { Subscription } from "rxjs";
import { BroadcastService } from "@bbwt/modules/broadcasting";

@Component({
    selector: "episode-dialog",
    styleUrls: ["./episode-dialog.component.scss"],
    template: `
        <p-dialog
            [(visible)]="visible"
            [style]="{ 'max-width': '700px', width: '100%' }"
            [modal]="modal"
            [closable]="closable"
            [draggable]="draggable"
            [header]="episodeId && episodeId !== '0' ? 'Update Episode' : 'Create Episode'"
            appendTo="body">
            <div class="flex flex-column pt-3">
                <div *ngIf="assignedClinicianVisible && (!assignedClinicianId || !assignedClinicianUserId.value)" [formGroup]="episodeForm" class="m-input-group">
                    <label class="m-input-group-name asterisk-mark">Assigned Clinician:</label>
                    <div class="m-input-group-value">
                        <m-select-clinician #clinician [formControl]="clinicianUserId"></m-select-clinician>
                        <control-errors [control]="clinicianUserId"></control-errors>
                    </div>
                </div>

                <form #diagnosisForm="ngForm">
                    <diagnosis name="diagnosis" [(ngModel)]="currentDiagnosis" (ngModelChange)="updateBoolValue()"></diagnosis>
                    <form-buttons
                        allowOffline
                        [config]="diagnosisButtonsConfig"
                        [disabled]="diagnosisForm.invalid"
                        layout="standard"></form-buttons>
                </form>

                <form [formGroup]="episodeForm" class="flex align-items-center justify-content-end mt-4 mb-2">


                    <div *ngIf ="showCheckBoxes"  class="checkboxes">
                        <p-checkbox
                            [disabled]="!privateEpisodesAllowed"
                            formControlName="private"
                            [binary]="true"
                            label="Private"
                            class="mr-4 ml-4 mb-2"></p-checkbox>

                        <p-checkbox
                            [disabled]="!significantAllowed"
                            formControlName="significant"
                            [binary]="true"
                            label="Significant"
                            class="mr-4 ml-4 mb-2"></p-checkbox>

                        <p-checkbox
                            [disabled]="!alwaysActiveAllowed"
                            formControlName="alwaysActive"
                            [binary]="true"
                            label="Always Active"
                            class="mr-4 ml-4 mb-2"></p-checkbox>
                    </div>


                    <button
                        *ngIf="diagnosesArray.length"
                        pButton
                        class="p-button-secondary p-button-text"
                        icon="mi mi-button-remove text-danger"
                        label="Delete All"
                        type="button"
                        (click)="clearDiagnosis()"></button>
                </form>

                <grid [tableSettings]="diagnosisTableSettings" [gridSettings]="diagnosisGridSettings" class="diaggrid">
                </grid>
                <div *ngIf="ravenModuleEnabled && ravenValidConfig" class="raven-header">
                    Raven
                </div>
                <raven-fields
                    #ravenControl="ngModel"
                    [(ngModel)]="currentRavenIssue"
                    [ngModelOptions]="{ standalone: true }"
                    [episodeId]="episodeId"
                    [showNotes]="!episodeId || episodeId === '0'"
                    *ngIf="ravenModuleEnabled && ravenValidConfig"></raven-fields>

                <form-buttons
                    #createEpisode
                    allowOffline
                    [config]="episodeButtonsConfig"
                    layout="centered"
                    [disabled]="episodeForm.invalid"
                    [saving]="savingEpisode || searchingDuplEpisodes">
                </form-buttons>
            </div>
        </p-dialog>

        <duplicate-episodes-dialog
            [modal]="true"
            [(visible)]="showDuplEpisodes"
            [duplicateEpisodes]="duplEpisodes"
            [duplicateBodyPartSide]="duplBodyPartSide"
            [tempEpisodeDupCheak]="tempEpisodeDupCheak"
            [isWalkInDraft]="isWalkInDraft"
            [isPrescription]="isPrescription"
            [isForm]="createFrom === EpisodeCreatedFrom.Form"
            [tempWalkIn]="tempWalkIn"
            [selectedWalkInDate]="selectedWalkInDate"
            (viewEpisode)="viewDuplEpisode($event)"
            (createEpisode)="confirmCreateEpisode()"
            (selectExistingEpisode)="selectExistingDuplEpisode($event)"></duplicate-episodes-dialog>
    `
})
export class EpisodeDialogComponent extends EpisodeBaseComponent implements OnInit, AfterViewInit, OnDestroy {
    private _visible = false;
    private _saved = false;

    selectedEpisode: IEpisode;

    currentDiagnosis: Diagnosis;
    currentRavenIssue: IRavenIssue;
    private onlineChangeSuscription: Subscription;
    isOnline: boolean = true;
    ravenModuleEnabled: boolean = false;

    ravenValidConfig: boolean = false;

    diagnosisButtonsConfig: FormButtonsConfig = {
        saveLabel: "Add Diagnosis",
        saveFunc: () => this.addDiagnosis(),
        cancelFunc: () => {
            this.currentDiagnosis = null;
            this.diagnosisControl.reset();
        },
        cancelLabel: "Clear"
    };

    episodeButtonsConfig: FormButtonsConfig = {
        saveLabel: this.episodeId ? "Update Episode" : "Create Episode",
        cancelFunc: () => {
            this.onCancel.emit(this.selectedEpisode);
            this.visible = false;
        },
        saveFunc: () => {
            this.saveEpisode();
        }
    };

    @Input() get visible() {
        return this._visible;
    }
    set visible(visible: boolean) {
        this._visible = visible;
        this.visibleChange.emit(visible);
        if (!visible && !this._saved) this.onHideDialog.emit();
    }
    @Input() modal = false;
    @Input() closable = false;
    @Input() draggable = false;

    @Input() createFrom: EpisodeCreatedFrom = EpisodeCreatedFrom.Prescription;

    @Input() restoreDuplicateCheakState: boolean;
    @Input() isWalkInDraft: boolean;
    @Input() tempWalkIn: WalkIn | any = null;
    @Input() selectedWalkInDate: Date = null;
    @Input() assignedClinicianId: string;
    @Input() assignedClinicianVisible: boolean = true;
    @Input() incidentRavenIssue: IRavenIssue = null;
    @Input() incidentNotes: string = null;

    @Input() showCheckBoxes = true;

    EpisodeCreatedFrom = EpisodeCreatedFrom;

    @Output() readonly visibleChange = new EventEmitter<boolean>();
    @Output() readonly onHideDialog = new EventEmitter<any>();
    @Output() readonly onCancel = new EventEmitter<IEpisode>();
    @Output() readonly viewEpisode = new EventEmitter<string>();
    @Output() readonly episodeCreated = new EventEmitter<IEpisode>();
    @Output() readonly episodeUpdated = new EventEmitter<IEpisode>();
    @Output() readonly selectExistingEpisode = new EventEmitter<IEpisode>();

    @ViewChild(DiagnosisControlComponent) diagnosisControl: DiagnosisControlComponent;
    @ViewChild("createEpisode") formButtons: FormButtonsComponent;
    @ViewChild(RavenControlComponent) ravenControl: RavenControlComponent;
    constructor(
        episodeService: EpisodeService,
        messageService: MessageService,
        fb: UntypedFormBuilder,
        patientService: PatientService,
        userHelperService: UserHelperService,
        public appOnlineStateService: AppOnlineStateService,
        public ravenApiService: RavenApiService,
        private broadcastService: BroadcastService,
    ) {
        super(episodeService, messageService, fb, patientService, userHelperService, appOnlineStateService);
    }

    async ngOnInit() {

        this.onlineChangeSuscription = this.broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .subscribe(data => {
                this.isOnline = data.isAppOnline;
            });

        this.isOnline = this.appOnlineStateService.isAppOnline;

        if (!this.createFrom) {
            this.createFrom = EpisodeCreatedFrom.Prescription;
          }

        if (this.assignedClinicianId) {
            this.setAssignedClinicianId(this.assignedClinicianId);
        }

        this.ravenModuleEnabled = this.ravenApiService.isRavenModuleEnabled();

        if (this.ravenModuleEnabled) {
            this.applyInitialRavenIssue();

            this.ravenValidConfig = await this.ravenApiService.ravenConfigIsValid();
            this.showWarningIfBadRavenConfig();
        }

        if (this.episodeId && this.episodeId !== "0") {
            const episode = await this.episodeService.get(this.episodeId);

            this.selectedEpisode = episode;

            this.privateEpisodesAllowed =
                this.userHelperService.hasPermission(Permissions.ConsultationHistoryPrivateEpisodeWrite) &&
                (!this.episodeId || this.userHelperService.userId === episode?.assignedClinicianUserId);

            if (this.ravenModuleEnabled && this.ravenValidConfig && episode.ravenIssue) {
                this.setCurrentRavenIssue(episode.ravenIssue);
            }
        } else this.privateEpisodesAllowed = this.userHelperService.hasPermission(Permissions.ConsultationHistoryPrivateEpisodeWrite);
    }

    async ngAfterViewInit() {
        if (this.restoreDuplicateCheakState && AppStorage.getItemFromSession("tempEpisodeDupCheak")) {
            this.restoreDuplicateCheakState = false;
            var episode = <IEpisode>AppStorage.getItemFromSession("tempEpisodeDupCheak");
            episode.createDateTime = new Date(episode.createDateTime);

            if (this.patientId == null) {
                this.patientId = episode.patientUserId;
            }
            await this.restoreEpisodes(episode);
            this.draftedEpisode = episode;
        }

        if (this.draftedEpisode) {
            this.selectedEpisode = this.draftedEpisode;
        }

        await this.initEpisode();
        this.applyInitialRavenIssue();
    }

    /**
     * Prefers Raven already associated with the episode/draft.
     * Falls back to the incident Raven only when creating a brand-new episode.
     */
    private applyInitialRavenIssue() {
        if (!this.ravenModuleEnabled) {
            return;
        }

        const episodeRavenIssue = this.draftedEpisode?.ravenIssue ?? this.selectedEpisode?.ravenIssue;
        if (episodeRavenIssue) {
            this.setCurrentRavenIssue(episodeRavenIssue);
            return;
        }

        const isNewEpisode = !this.episodeId || this.episodeId === "0";
        if (isNewEpisode && !this.draftedEpisode && this.incidentRavenIssue) {
            this.setCurrentRavenIssue(this.incidentRavenIssue, this.incidentNotes);
        }
    }

    private setCurrentRavenIssue(issue: IRavenIssue, notes?: string) {
        this.currentRavenIssue = {
            ...issue,
            ravenCategories: issue.ravenCategories ? [...issue.ravenCategories] : [],
            ravenCategoriesIds: issue.ravenCategoriesIds ? [...issue.ravenCategoriesIds] : []
        };
        if (notes) {
            this.currentRavenIssue.ravenNotes = notes;
        }
        this.ravenIssue = this.currentRavenIssue;
        this.ravenControl?.writeValue(this.currentRavenIssue);
    }

    ngOnDestroy(): void {
        this.onlineChangeSuscription?.unsubscribe();
    }

    private showWarningIfBadRavenConfig() {
        if (!this.ravenValidConfig) {
            this.messageService.add(Message.Warning("Raven configuration is not valid. Please check", "Raven Configuration"));
        }
    }

    viewDuplEpisode(episodeId: string): void | Promise<void> {
        this.viewEpisode.emit(episodeId);
    }

    protected updateBoolValue() {
        this.alwaysActiveAllowed = this.checkDiagnoses();
    }

    addDiagnosis() {
        if ((!this.currentDiagnosis || !this.currentDiagnosis.code) && this.isOnline) {
            this.messageService.add(Message.Error("You cannot add a diagnosis without code while ONLINE. You can only do this from the Consultation History, which will create an OFFLINE episode."));
            return;
        }

        super.addDiagnosis(this.currentDiagnosis);
        this.diagnosisControl.reset();
        this.currentDiagnosis = null;
        this.diagnosesArray.value

        this.alwaysActiveAllowed = this.checkDiagnoses();

    }

    checkDiagnoses(): boolean {
        const diagnoses = this.diagnosesArray.value;
        const currentDiagnosis = this.currentDiagnosis;

        if ((diagnoses.length === 0 || diagnoses.length > 1) && !currentDiagnosis) {

            return true;
        }

        const allDiagnoses = [...diagnoses];
        if (currentDiagnosis) {
          allDiagnoses.push(currentDiagnosis);
        }

        const isAlwaysActiveAllowed = allDiagnoses.every(diagnosis =>
          !(
            diagnosis.code === GenericDiagnosisCodes.RoutineDiagnosisCode ||
            diagnosis.code === GenericDiagnosisCodes.RoutineDiagnosisName ||
            diagnosis.code === GenericDiagnosisCodes.GenericPrescriptionDiagnosisCode ||
            diagnosis.code === GenericDiagnosisCodes.GymRecoveryDiagnosisCode ||
            diagnosis.name ===  GenericDiagnosisCodes.GenericPrescriptionDiagnosisName
          )
        );

        if (!isAlwaysActiveAllowed) {
          this.episodeForm.patchValue({ alwaysActive: false });
        }

        return isAlwaysActiveAllowed;
      }

    addRavenIssue() {
        this.currentRavenIssue.ravenCategoriesIds = (this.currentRavenIssue.ravenCategories ?? []).map(c => c.id);
        super.addRavenIssue(this.currentRavenIssue);
    }

    checkRavenFields() {
        return !(
            !this.currentRavenIssue.ravenPriority ||
            !this.currentRavenIssue.ravenCategories ||
            this.currentRavenIssue.ravenCategories.length === 0 ||
            !this.currentRavenIssue.ravenLog ||
            !this.currentRavenIssue.ravenLocation
        );
    }

    async saveEpisode() {
        if (this.ravenModuleEnabled && this.ravenValidConfig) {
            if (!this.checkRavenFields()) {
                this.messageService.add(Message.Error("You need to complete the raven fields"));
                return;
            }
          this.addRavenIssue();
        }

        if (!this.checkDiagnosesForCode() && this.isOnline) {
            this.messageService.add(Message.Error("You cannot add a diagnosis without code while ONLINE. You can only do this from the Consultation History, which will create an OFFLINE episode."));
            return;
        }

        const isDraftPrescription = this.draftedEpisode?.state === EpisodeStatus.DraftPrescription;
        const shouldSaveDraft = !this.episodeId || isDraftPrescription;

        await super.save(shouldSaveDraft && this.isPrescription, this.draftedEpisode ?? null);
        this._saved = true;
      }

      checkDiagnosesForCode() {
        return this.diagnosesArray.value.every(diagnosis => !!diagnosis.code);
      }

    protected async updateEpisode(): Promise<IEpisode> {
        const updatedEpisode = await super.updateEpisode();
        this.episodeUpdated.emit(updatedEpisode);
        this.visible = false;
        return updatedEpisode;
    }

    protected async createEpisode(isDraft: boolean = false): Promise<IEpisode> {
        let createdEpisode: IEpisode;

        if (this.alwaysActiveAllowed) {
          createdEpisode = await super.createEpisode(isDraft);
        } else {
          createdEpisode = await super.createEpisode(isDraft,false, true);
        }

        createdEpisode.createFrom = this.createFrom;
        this.episodeCreated.emit(createdEpisode);
        this.visible = false;
        return createdEpisode;
      }

    selectExistingDuplEpisode(episodeId: string): IEpisode {
        const existingEpisode = super.selectExistingDuplEpisode(episodeId);
        if (existingEpisode) {
            this.selectExistingEpisode.emit(existingEpisode);
            this.visible = false;
        }
        return existingEpisode;
    }
}
